import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import DropDown from '../components/dropdown/DropDown';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Spinner from '../components/Spinner/spinner';
import Icon from '../components/icon/Icon';
import { useConfig } from '../context/ConfigContext';
import CommissionIntervalsEditor, {
  EditableCommissionInterval,
} from '../organisms/Settings/CommissionIntervalsEditor';
import {
  COMMISSIONS_HEADER_BG,
  commissionsTableStyles,
} from './CommissionsConfiguration.styles';
import theme from '../styles/theme';
import { routes } from '../routes/routePaths';
import { Toast, useToast } from '../components/toast/toast';
import {
  CommissionValidationField,
  validateCommissionRowsDetailed,
} from '../utils/commissionIntervalValidation';
import { mapEditableToSavePayload, formatCommissionCurrencyDisplay } from '../utils/commissionConfigurationPayload';
import {
  CommissionCalculationType,
  CommissionConfigurationRow,
  getCommissionConfiguration,
  saveCommissionConfiguration,
} from '../services/CommissionsConfigurationService';
import { showCommissionsConfigurationSection } from '../utils/settingsTenantSections';

type EditableRow = CommissionConfigurationRow & {
  rowId: number;
  expenseRateInput: string;
  intervalsInput: EditableCommissionInterval[];
};

const buildRowKey = (productLineId: number, currencyId: number): number =>
  productLineId * 10000 + currencyId;

const formatRateDisplay = (value: number | null): string => {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const mapRowToEditable = (row: CommissionConfigurationRow, index: number): EditableRow => ({
  ...row,
  rowId: buildRowKey(row.productLineId, row.currencyId) + index,
  expenseRateInput: formatRateDisplay(row.expenseRate),
  intervalsInput: row.intervals.map((interval) => ({
    fromMonth: interval.fromMonth != null ? String(interval.fromMonth) : '',
    toMonth: interval.toMonth != null ? String(interval.toMonth) : '',
    rate: formatRateDisplay(interval.rate),
  })),
});

const buildValidationRows = (rows: EditableRow[]) =>
  rows.map((row) => {
    const payload = mapEditableToSavePayload(row);
    return {
      productLineLabel: row.productLineLabel,
      currencyCode: row.currencyCode,
      currencyLabel: formatCommissionCurrencyDisplay(row.currencyCode),
      rowKey: buildRowKey(row.productLineId, row.currencyId),
      calculationTypeId: payload.calculationTypeId,
      expenseRate: payload.expenseRate,
      intervals: payload.intervals,
    };
  });

const CommissionsConfiguration = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const canAccessCommissionsConfiguration = showCommissionsConfigurationSection(config);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculationTypes, setCalculationTypes] = useState<CommissionCalculationType[]>([]);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<number>>(new Set());
  const [rowErrorKeys, setRowErrorKeys] = useState<Set<number>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Map<number, CommissionValidationField>>(
    new Map(),
  );
  const [originalSnapshot, setOriginalSnapshot] = useState('');

  const isDirty = useMemo(() => {
    if (!originalSnapshot) return false;
    return JSON.stringify(rows) !== originalSnapshot;
  }, [rows, originalSnapshot]);

  const calculationTypeItems = useMemo(
    () => calculationTypes.map((type) => ({ label: type.label })),
    [calculationTypes],
  );

  const clearValidationState = () => {
    setRowErrorKeys(new Set());
    setFieldErrors(new Map());
  };

  const applyValidationError = (rowKey?: number, field?: CommissionValidationField) => {
    if (rowKey == null) {
      return;
    }

    const matchingRow = rows.find(
      (row) => buildRowKey(row.productLineId, row.currencyId) === rowKey,
    );
    if (!matchingRow) {
      return;
    }

    setRowErrorKeys(new Set([rowKey]));
    setFieldErrors(field ? new Map([[rowKey, field]]) : new Map());
    setExpandedRowIds((current) => {
      const next = new Set(current);
      next.add(matchingRow.rowId);
      return next;
    });
  };

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCommissionConfiguration();
      if (response.success && response.data) {
        setCalculationTypes(response.data.calculationTypes ?? []);
        const editableRows = (response.data.rows ?? []).map(mapRowToEditable);
        setRows(editableRows);
        setOriginalSnapshot(JSON.stringify(editableRows));
        clearValidationState();
      } else {
        useToast.error({
          title: response.message || 'Error al cargar comisiones',
          id: 'commissions-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch {
      useToast.error({
        title: 'Error al cargar comisiones',
        id: 'commissions-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccessCommissionsConfiguration) {
      return;
    }
    loadConfiguration();
  }, [loadConfiguration, canAccessCommissionsConfiguration]);

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const updateRow = (rowId: number, updater: (row: EditableRow) => EditableRow) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.rowId === rowId ? updater(row) : row)),
    );
  };

  const handleCalculationTypeChange = (rowId: number, label: string) => {
    const selectedType = calculationTypes.find((type) => type.label === label);
    updateRow(rowId, (row) => ({
      ...row,
      calculationTypeId: selectedType?.id ?? null,
    }));
    clearValidationState();
  };

  const handleExpenseRateChange = (rowId: number, value: string) => {
    updateRow(rowId, (row) => ({
      ...row,
      expenseRateInput: value,
    }));
    clearValidationState();
  };

  const handleAddInterval = (rowId: number) => {
    updateRow(rowId, (row) => {
      const last = row.intervalsInput[row.intervalsInput.length - 1];
      const parsedToMonth = last ? Number(last.toMonth) : NaN;
      const nextFrom = Number.isFinite(parsedToMonth) ? String(parsedToMonth + 1) : '';
      return {
        ...row,
        intervalsInput: [...row.intervalsInput, { fromMonth: nextFrom, toMonth: '', rate: '' }],
      };
    });
    setExpandedRowIds((current) => new Set(current).add(rowId));
    clearValidationState();
  };

  const handleRemoveInterval = (rowId: number, intervalIndex: number) => {
    updateRow(rowId, (row) => ({
      ...row,
      intervalsInput: row.intervalsInput.filter((_, index) => index !== intervalIndex),
    }));
    clearValidationState();
  };

  const handleIntervalChange = (
    rowId: number,
    intervalIndex: number,
    field: keyof EditableCommissionInterval,
    value: string,
  ) => {
    updateRow(rowId, (row) => ({
      ...row,
      intervalsInput: row.intervalsInput.map((interval, index) =>
        index === intervalIndex ? { ...interval, [field]: value } : interval,
      ),
    }));
    clearValidationState();
  };

  const handleEditIntervals = (rowId: number) => {
    setExpandedRowIds((current) => new Set(current).add(rowId));
  };

  const renderExpandedIntervals = (props: { data: EditableRow }) => {
    const row = props.data;

    return (
      <CommissionIntervalsEditor
        intervals={row.intervalsInput}
        onAddInterval={() => handleAddInterval(row.rowId)}
        onRemoveInterval={(intervalIndex) => handleRemoveInterval(row.rowId, intervalIndex)}
        onIntervalChange={(intervalIndex, field, value) =>
          handleIntervalChange(row.rowId, intervalIndex, field, value)
        }
      />
    );
  };

  const columns = [
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Producto
        </P>
      ),
      selector: (row: EditableRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {row.productLineLabel}
        </P>
      ),
      minWidth: '120px',
      grow: 2,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Moneda
        </P>
      ),
      selector: (row: EditableRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {formatCommissionCurrencyDisplay(row.currencyCode)}
        </P>
      ),
      minWidth: '80px',
      grow: 1,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Tipo
        </P>
      ),
      selector: (row: EditableRow) => {
        const rowKey = buildRowKey(row.productLineId, row.currencyId);
        const selectedLabel =
          calculationTypes.find((type) => type.id === row.calculationTypeId)?.label ?? 'Seleccionar';
        const hasFieldError = fieldErrors.get(rowKey) === 'tipo';

        return (
          <div
            className="commissions-configuration-cell-dropdown"
            onClick={(event) => event.stopPropagation()}
          >
            <DropDown
              id={`commission-type-${row.rowId}`}
              title={selectedLabel}
              items={calculationTypeItems}
              onChange={(label) => handleCalculationTypeChange(row.rowId, label)}
              size="sm"
              variant="instrumentation"
              marginTop="0"
              status={hasFieldError ? 'error' : 'normal'}
            />
          </div>
        );
      },
      minWidth: '160px',
      grow: 3,
      allowOverflow: true,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Gasto %
        </P>
      ),
      selector: (row: EditableRow) => {
        const rowKey = buildRowKey(row.productLineId, row.currencyId);
        const hasFieldError = fieldErrors.get(rowKey) === 'gasto';

        return (
          <div
            className="commissions-configuration-cell-input"
            onClick={(event) => event.stopPropagation()}
          >
            <Input
              size="sm"
              value={row.expenseRateInput}
              onChange={(event) => handleExpenseRateChange(row.rowId, event.target.value)}
              placeholder="0,00"
              inputStatus={hasFieldError ? 'error' : 'normal'}
            />
          </div>
        );
      },
      minWidth: '100px',
      grow: 2,
    },
    {
      name: (
        <P variant="secondary-2" type="3" weight="bold" withMargin={false}>
          Intervalos
        </P>
      ),
      selector: (row: EditableRow) => (
        <P variant="secondary-2" type="3" weight="regular" withMargin={false}>
          {row.intervalsInput.length}
        </P>
      ),
      minWidth: '90px',
      grow: 1,
      center: true,
    },
    {
      name: '',
      cell: (row: EditableRow) => (
        <span
          className="commissions-edit-icon"
          onClick={() => handleEditIntervals(row.rowId)}
          title="Editar intervalos"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleEditIntervals(row.rowId)}
        >
          <Icon name="edit_outline" width={20} height={20} />
        </span>
      ),
      width: '56px',
      center: true,
      button: true,
    },
  ];

  const handleSave = async () => {
    const validationError = validateCommissionRowsDetailed(buildValidationRows(rows));

    if (validationError) {
      applyValidationError(validationError.rowKey, validationError.field);
      useToast.error({
        title: validationError.message,
        id: 'commissions-toast',
        description: '',
        closeButton: true,
        autoClose: 6000,
      });
      return;
    }

    clearValidationState();
    setSaving(true);
    try {
      const payloadRows = rows.map(mapEditableToSavePayload);
      const response = await saveCommissionConfiguration(payloadRows);
      if (response.success && response.data) {
        useToast.success({
          title: 'Comisiones guardadas correctamente',
          id: 'commissions-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        setCalculationTypes(response.data.calculationTypes ?? []);
        const savedRows = (response.data.rows ?? []).map(mapRowToEditable);
        setRows(savedRows);
        setOriginalSnapshot(JSON.stringify(savedRows));
      } else {
        useToast.error({
          title: response.message || 'Error al guardar comisiones',
          id: 'commissions-toast',
          description: '',
          closeButton: true,
          autoClose: 6000,
        });
      }
    } catch (error: any) {
      useToast.error({
        title: error?.message || 'Error al guardar comisiones',
        id: 'commissions-toast',
        description: '',
        closeButton: true,
        autoClose: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row: EditableRow) =>
          rowErrorKeys.has(buildRowKey(row.productLineId, row.currencyId)),
        style: {
          backgroundColor: '#FFF5F5',
        },
      },
    ],
    [rowErrorKeys],
  );

  if (!canAccessCommissionsConfiguration) {
    return <Navigate to={routes.settings} replace />;
  }

  return (
    <Card variant="neutral" modifier="inverted" spacing="4" height="-webkit-fill-available" width="85vw">
      <style>{commissionsTableStyles}</style>

      <Card display="flex" justifyContent="space-between" alignItems="center" margin={{ bottom: '3' }}>
        <Card display="flex" alignItems="center" gap="12px">
          <Button variant="secondary" size="sm" onClick={handleGoBack}>
            <Icon name="arrow_back" width={20} height={20} />
          </Button>
          <H withMargin={false} type="1" weight="bold" modifier="strong">
            Comisiones
          </H>
        </Card>
      </Card>

      {loading ? (
        <Card>
          <TableSkeleton rows={8} columns={6} />
        </Card>
      ) : (
        <div className="commissions-configuration-table">
          <Table
            keyField="rowId"
            columns={columns}
            data={rows}
            tableData={{
              page: 0,
              size: rows.length,
              totalElements: rows.length,
              totalPages: 1,
            }}
            disablePagination
            expandableRows
            expandableRowsComponent={renderExpandedIntervals}
            expandableRowExpanded={(row: EditableRow) => expandedRowIds.has(row.rowId)}
            onRowExpandToggled={(expanded: boolean, row: EditableRow) => {
              setExpandedRowIds((current) => {
                const next = new Set(current);
                if (expanded) {
                  next.add(row.rowId);
                } else {
                  next.delete(row.rowId);
                }
                return next;
              });
            }}
            conditionalRowStyles={conditionalRowStyles}
            customStyles={{
              head: {
                style: {
                  backgroundColor: COMMISSIONS_HEADER_BG,
                },
              },
              headRow: {
                style: {
                  backgroundColor: COMMISSIONS_HEADER_BG,
                  minHeight: '48px',
                  borderBottom: 'none',
                  gap: 0,
                  width: '100%',
                },
              },
              headCells: {
                style: {
                  backgroundColor: COMMISSIONS_HEADER_BG,
                  color: theme.colors.neutral.text.strong,
                  fontWeight: 600,
                  fontSize: 14,
                  paddingLeft: '12px',
                  paddingRight: '12px',
                },
              },
              cells: {
                style: {
                  alignItems: 'center',
                  backgroundColor: theme.colors.neutral.bg.inverted,
                },
              },
              responsiveWrapper: {
                style: {
                  width: '100%',
                  borderTop: 'none',
                  borderBottom: 'none',
                  overflow: 'visible',
                },
              },
              table: {
                style: {
                  width: '100%',
                },
              },
            }}
          />
        </div>
      )}

      <Card display="flex" justifyContent="flex-end" margin={{ top: '4' }}>
        <Button variant="primary" size="lg" onClick={handleSave} disabled={loading || saving || !isDirty}>
          {saving ? <Spinner size="sm" /> : 'Guardar'}
        </Button>
      </Card>

      <Toast id="commissions-toast" position="top-right" />
    </Card>
  );
};

export default CommissionsConfiguration;
